import config from '@config';
import { Metric } from '@enums';
import { themeOptions } from '@theme';
import type { ChartData, ChartType } from 'types/common';

export const DEFAULT_METRIC: Record<ChartType, Metric> = {
  bar: Metric.Universe,
  pie: Metric.AudiencePercentage,
  'stacked-bar': Metric.AudiencePercentage
};

export const MAX_VALUES: Record<ChartType, number> = {
  bar: undefined,
  pie: 8,
  'stacked-bar': 4
};

export function formatMetricLabel(metric: Metric): string {
  if (metric === Metric.AudiencePercentage) {
    return 'Audience %';
  }

  if (metric === Metric.Universe) {
    return 'Universe';
  }

  return 'Index';
}

export function getChartColor(colorName: string): string {
  if (colorName in themeOptions.palette) {
    const color =
      themeOptions.palette[colorName as keyof typeof themeOptions.palette];

    if (color && typeof color === 'object') {
      if ('medium' in color) {
        return String(color['medium']);
      }

      if ('500' in color) {
        return String(color['500']);
      }
    }
  }

  return themeOptions.palette.grey['500'];
}

/** Same token order as InsightVisualization for bar audience colors. */
export const INSIGHT_MINI_CHART_COLOR_NAMES = [
  'violet',
  'teal',
  'pink',
  'blue',
  'yellow',
  'purple',
  'green',
  'red',
  'cyan',
  'orange'
] as const;

export function resolveBarColors(
  chartData: ChartData[],
  preferAudienceColor: boolean
): string[] {
  return chartData.map(({ audience }, index) =>
    getChartColor(
      audience && preferAudienceColor
        ? audience.color
        : INSIGHT_MINI_CHART_COLOR_NAMES[
            index % INSIGHT_MINI_CHART_COLOR_NAMES.length
          ]
    )
  );
}

/** Minimum card width (px) before inline insight charts become unreadable. */
export function getInsightChartMinWidth(type: ChartType): number {
  return config.chart.insightBlock.minWidth[type];
}

export const isSupportedChartType = (type: ChartType): boolean => {
  return type === 'bar' || type === 'stacked-bar' || type === 'pie';
};
